import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { RotateCcw, UserRound, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { BrandWordmark } from "@/components/flow/Brand";
import { Composer, Thread, caseStatusLine } from "@/components/flow/SupportThread";
import { publicApi, errMsg } from "@/lib/api";
import { EASE } from "@/lib/motion";
import { t } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------------------
   Public website chat — sales & support for Listed.
   A quiet ink pill bottom-right opens a 392px panel (full-screen on phones).
   The assistant answers from Listed's knowledge; "Talk to a person" (or the
   assistant itself, when it cannot help) hands the conversation to Listed HQ,
   who reply here and by e-mail. The visitor's session lives in localStorage
   for a week so they can come back to the same thread.
--------------------------------------------------------------------------- */

/** Listed's chat glyph — a soft bubble with three dots that gently breathe while the assistant is live. */
export function ChatGlyph({ className, live = true, size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <path d="M12 3.6c-4.97 0-9 3.2-9 7.15 0 2.02 1.06 3.84 2.77 5.13l-.7 2.9c-.15.6.49 1.1 1.05.82l3.35-1.7c.8.2 1.65.3 2.53.3 4.97 0 9-3.2 9-7.15S16.97 3.6 12 3.6Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      {[8.4, 12, 15.6].map((cx, i) => (
        <circle key={cx} cx={cx} cy="10.9" r="1.15" fill="var(--lavender-soft)" className={live ? "glyph-dot" : undefined} style={live ? { animationDelay: `${i * 160}ms` } : undefined} />
      ))}
    </svg>
  );
}

const LS_KEY = "listed.support.session";
const WEEK = 7 * 24 * 3600 * 1000;
const EPOCH = "1970-01-01T00:00:00+00:00";
const QUICK = ["sup.q1", "sup.q2", "sup.q3", "sup.q4"];

function loadSession() {
  try {
    const raw = JSON.parse(localStorage.getItem(LS_KEY) || "null");
    if (!raw?.id || !raw?.token || Date.now() - (raw.at || 0) > WEEK) return null;
    return raw;
  } catch {
    return null;
  }
}
function saveSession(s) {
  try {
    if (s) localStorage.setItem(LS_KEY, JSON.stringify({ ...s, at: Date.now() }));
    else localStorage.removeItem(LS_KEY);
  } catch {
    /* private mode */
  }
}

const isActive = (c) => c && c.status !== "resolved" && c.status !== "closed";
const humanOwned = (c) => c && !c.ai_handled && (c.status === "waiting_on_hq" || c.status === "waiting_on_customer");

/** Inline card: leave your details so a person can follow up. */
function HandoffCard({ contact, onSubmit, onCancel, busy, required = true }) {
  const [f, setF] = useState({ name: contact?.name || "", email: contact?.email || "", company: contact?.company || "", message: "" });
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) {
      setErr(t("sup.handoff.email_invalid"));
      return;
    }
    setErr("");
    await onSubmit({ name: f.name.trim(), email: f.email.trim(), company: f.company.trim(), message: f.message.trim() });
  };
  const input = "w-full h-10 rounded-[10px] border border-ink/[0.12] bg-surface px-3 text-[13.5px] text-ink placeholder:text-ink/40 transition-[border-color,box-shadow] duration-150 hover:border-ink/25 focus:outline-none focus:border-[var(--focus-border)] focus:shadow-ring";
  return (
    <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22, ease: EASE }} onSubmit={submit} className="rounded-[16px] border border-hairline bg-surface p-3.5 shadow-soft" data-testid="support-handoff-form">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-lavender/15 text-lavender"><UserRound className="h-3.5 w-3.5" strokeWidth={2} /></span>
        <div className="min-w-0">
          <p className="text-[13.5px] font-medium text-ink">{t("sup.handoff.title")}</p>
          <p className="text-[12.5px] text-quiet leading-[1.45] mt-0.5">{t("sup.handoff.sub")}</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <input className={input} placeholder={t("sup.handoff.name")} value={f.name} onChange={set("name")} autoComplete="name" data-testid="support-handoff-name" />
        <input className={input} placeholder={t("sup.handoff.company")} value={f.company} onChange={set("company")} autoComplete="organization" data-testid="support-handoff-company" />
        <input className={cn(input, "col-span-2")} type="email" required={required} placeholder={t("sup.handoff.email")} value={f.email} onChange={set("email")} autoComplete="email" aria-invalid={err ? "true" : undefined} data-testid="support-handoff-email" />
        <textarea className={cn(input, "col-span-2 h-auto min-h-[64px] py-2 resize-none leading-[1.45]")} placeholder={t("sup.handoff.message")} value={f.message} onChange={set("message")} rows={2} data-testid="support-handoff-message" />
      </div>
      {err && <p className="mt-2 text-[12px] text-[#B42318]" role="alert" data-testid="support-handoff-error">{err}</p>}
      <div className="mt-3 flex items-center justify-end gap-2">
        {onCancel && <button type="button" onClick={onCancel} className="h-9 px-3 rounded-[10px] text-[13px] font-medium text-quiet hover:text-ink hover:bg-fill transition-colors focus-visible:outline-none focus-visible:shadow-ring" data-testid="support-handoff-cancel">{t("common.cancel")}</button>}
        <button type="submit" disabled={busy} className="h-9 px-4 rounded-[10px] bg-ink text-white text-[13px] font-medium hover:bg-[#2A2620] transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-ring" data-testid="support-handoff-submit">{busy ? t("sup.handoff.sending") : t("sup.handoff.submit")}</button>
      </div>
    </motion.form>
  );
}

export function SupportChat() {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => loadSession());
  const [kase, setKase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [handoff, setHandoff] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [mobile, setMobile] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const f = () => setMobile(mq.matches);
    f();
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);

  useEffect(() => {
    publicApi.get("/public/support/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const hdr = useMemo(() => (session ? { headers: { "X-Support-Token": session.token } } : {}), [session]);

  /** Pull the thread. `quiet` keeps unread counts (used while the panel is closed). */
  const refresh = useCallback(
    async (quiet = false) => {
      if (!session) return;
      try {
        const r = await publicApi.get(`/public/support/sessions/${session.id}`, { ...hdr, params: quiet ? { since: EPOCH } : {} });
        setKase(r.data.case);
        setMessages(r.data.messages || []);
        setError("");
      } catch (e) {
        if (e?.response?.status === 404) {
          saveSession(null);
          setSession(null);
          setKase(null);
          setMessages([]);
        }
      } finally {
        setLoaded(true);
      }
    },
    [session, hdr],
  );

  // First load + polling: 6s while open on an active case, 45s while closed (to light the badge).
  // Keyed on session/open/case status only — `busy` is read through a ref so a send never restarts the timer.
  const kaseActive = !kase || isActive(kase);
  useEffect(() => {
    if (!session) {
      setLoaded(true);
      return undefined;
    }
    refresh(!openRef.current);
    if (!kaseActive) return undefined;
    const ms = open ? 6000 : 45000;
    const id = setInterval(() => {
      if (document.hidden || busyRef.current) return;
      refresh(!openRef.current);
    }, ms);
    return () => clearInterval(id);
  }, [session, open, kaseActive, refresh]);

  // ESC closes; lock body scroll on phones while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    if (mobile) document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, mobile]);

  const startSession = async (text) => {
    const r = await publicApi.post("/public/support/sessions", { text: text || undefined, page: pathname, url: window.location.href });
    const s = { id: r.data.case.id, token: r.data.token };
    saveSession(s);
    setSession(s);
    setKase(r.data.case);
    setMessages(r.data.messages || []);
    return { s, data: r.data };
  };

  const send = async (text) => {
    setError("");
    setBusy(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: "customer", text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    const expectAssistant = cfg?.ai && !humanOwned(kase);
    if (expectAssistant) setTyping(true);
    try {
      if (!session) {
        await startSession(text);
      } else {
        const r = await publicApi.post(`/public/support/sessions/${session.id}/messages`, { text }, hdr);
        setMessages((m) => [...m.filter((x) => x.id !== optimistic.id), ...(r.data.messages || [])]);
        setKase(r.data.case);
      }
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setError(errMsg(e, t("sup.error")));
    } finally {
      setTyping(false);
      setBusy(false);
    }
  };

  const submitHandoff = async (form) => {
    setBusy(true);
    setError("");
    try {
      let s = session;
      if (!s) s = (await startSession()).s;
      const r = await publicApi.post(`/public/support/sessions/${s.id}/handoff`, form, { headers: { "X-Support-Token": s.token } });
      setKase(r.data.case);
      setMessages(r.data.messages || []);
      setHandoff(false);
    } catch (e) {
      setError(errMsg(e, t("sup.error")));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    saveSession(null);
    setSession(null);
    setKase(null);
    setMessages([]);
    setHandoff(false);
    setError("");
  };

  if (!cfg || cfg.enabled === false) return null;
  const unread = !open && kase ? kase.unread_customer || 0 : 0;
  const needsContact = kase && kase.status === "waiting_on_hq" && !kase.contact?.email;
  const showHandoff = (handoff || needsContact) && isActive(kase || { status: "open" });
  const closed = kase && !isActive(kase);
  const greeting = cfg.greeting || t("sup.greeting");
  const statusLine = kase ? caseStatusLine(kase) : cfg.ai ? t("sup.status.ai") : t("sup.status.team");

  return (
    <>
      {/* Launcher — a 56px ink disc with Listed's own chat glyph. Idle it breathes a soft lavender halo;
          hover slides the label out; open (desktop) morphs the glyph into a cross. Hidden on phones while open. */}
      <AnimatePresence initial={false}>
        {!(open && mobile) && (
          <motion.div key="launcher" initial={{ opacity: 0, y: reduced ? 0 : 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: reduced ? 0 : 10 }} transition={{ duration: 0.22, ease: EASE }} className="group/launch fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-[61] flex items-center gap-3" data-testid="support-launcher-wrap">
            {!open && (
              <span className="pointer-events-none hidden sm:inline-flex h-9 items-center rounded-full border border-hairline bg-surface/95 px-3.5 text-[13px] font-medium text-ink shadow-soft opacity-0 translate-x-2 transition-[opacity,transform] duration-200 ease-editorial group-hover/launch:opacity-100 group-hover/launch:translate-x-0 group-focus-within/launch:opacity-100 group-focus-within/launch:translate-x-0" aria-hidden data-testid="support-launcher-label">
                {t("sup.launcher")}
              </span>
            )}
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? t("common.close") : t("sup.open")}
              aria-expanded={open}
              className="relative inline-flex h-14 w-14 items-center justify-center rounded-full bg-ink text-white shadow-[0_18px_40px_-12px_rgba(21,19,15,0.45),0_0_0_1px_rgba(255,255,255,0.06)_inset] transition-[transform,box-shadow] duration-200 ease-editorial hover:-translate-y-0.5 hover:shadow-[0_22px_48px_-12px_rgba(21,19,15,0.5),0_0_0_1px_rgba(255,255,255,0.08)_inset] active:translate-y-0 active:scale-[0.97] focus-visible:outline-none focus-visible:shadow-ring"
              data-testid="support-launcher"
              data-open={open ? "true" : "false"}
            >
              {/* halo */}
              {!open && !reduced && <span aria-hidden className="support-halo absolute -inset-1.5 rounded-full bg-lavender/30 blur-md" />}
              <span className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-full">
                <AnimatePresence initial={false} mode="wait">
                  {open ? (
                    <motion.span key="x" initial={{ opacity: 0, rotate: reduced ? 0 : -90, scale: 0.7 }} animate={{ opacity: 1, rotate: 0, scale: 1 }} exit={{ opacity: 0, rotate: reduced ? 0 : 90, scale: 0.7 }} transition={{ duration: 0.2, ease: EASE }} className="flex">
                      <X className="h-[22px] w-[22px]" strokeWidth={2} />
                    </motion.span>
                  ) : (
                    <motion.span key="glyph" initial={{ opacity: 0, rotate: reduced ? 0 : 90, scale: 0.7 }} animate={{ opacity: 1, rotate: 0, scale: 1 }} exit={{ opacity: 0, rotate: reduced ? 0 : -90, scale: 0.7 }} transition={{ duration: 0.2, ease: EASE }} className="flex">
                      <ChatGlyph live={!reduced} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </span>
              {unread > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-lavenderCta px-1.5 text-[11px] font-semibold text-ink tabular-nums ring-[3px] ring-canvas" data-testid="support-unread-badge">{unread}</span>
              )}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.section
            key="panel"
            role="dialog"
            aria-modal={mobile ? "true" : undefined}
            aria-label={t("sup.title")}
            initial={{ opacity: 0, y: reduced ? 0 : 14, scale: reduced ? 1 : 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: reduced ? 0 : 14, scale: reduced ? 1 : 0.98 }}
            transition={{ duration: 0.24, ease: EASE }}
            className={cn("fixed z-[60] flex flex-col overflow-hidden bg-surface text-ink", mobile ? "inset-0 h-dvh" : "bottom-[100px] right-6 w-[392px] h-[min(640px,calc(100dvh-124px))] rounded-[24px] border border-hairline shadow-[0_30px_90px_-20px_rgba(21,19,15,0.35),0_0_0_1px_rgba(21,19,15,0.04)]")}
            data-testid="support-panel"
            data-status={kase?.status || "new"}
          >
            {/* Header */}
            <header className="shrink-0 bg-ink text-white px-4 pt-4 pb-3.5 grain grain-dark relative">
              <div className="relative z-[1] flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <BrandWordmark size="sm" light byline={false} />
                  <p className="mt-1.5 flex items-center gap-2 text-[12.5px] text-white/60 min-w-0" data-testid="support-status-line">
                    <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", closed ? "bg-white/30" : "bg-success pulse-dot")} />
                    <span className="truncate">{statusLine}</span>
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1 -mr-1.5 -mt-1">
                  {!closed && !humanOwned(kase) && (
                    <button type="button" onClick={() => setHandoff((h) => !h)} aria-label={t("sup.talk_to_person")} title={t("sup.talk_to_person")} aria-pressed={handoff} className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12.5px] font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:shadow-ring" data-testid="support-handoff-button">
                      <UserRound className="h-3.5 w-3.5" strokeWidth={2} /> <span className="hidden xs:inline">{t("sup.talk_to_person")}</span>
                    </button>
                  )}
                  {closed && (
                    <button type="button" onClick={reset} className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[12.5px] font-medium text-white/80 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:shadow-ring" data-testid="support-new-conversation">
                      <RotateCcw className="h-3.5 w-3.5" strokeWidth={2} /> {t("sup.new_conversation")}
                    </button>
                  )}
                  <button type="button" onClick={() => setOpen(false)} aria-label={t("common.close")} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:shadow-ring" data-testid="support-close">
                    <X className="h-[18px] w-[18px]" />
                  </button>
                </div>
              </div>
            </header>

            {/* Thread */}
            <Thread
              messages={messages}
              typing={typing}
              testId="support-thread"
              emptyState={null}
              footer={
                <>
                  {showHandoff && <HandoffCard contact={kase?.contact} onSubmit={submitHandoff} onCancel={needsContact ? undefined : () => setHandoff(false)} busy={busy} />}
                  {error && (
                    <div className="flex items-center justify-between gap-3 rounded-[12px] border border-[rgba(180,35,24,0.18)] bg-[#FCEBE9]/70 px-3 py-2 text-[12.5px] text-[#9A1F15]" role="alert" data-testid="support-error">
                      <span className="min-w-0 truncate">{error}</span>
                      <button type="button" onClick={() => setError("")} className="shrink-0 font-medium hover:underline" data-testid="support-error-dismiss">{t("common.close")}</button>
                    </div>
                  )}
                  {closed && (
                    <div className="text-center pt-2" data-testid="support-closed-footer">
                      <p className="text-[12.5px] text-quiet">{t("sup.closed_note")}</p>
                      <button type="button" onClick={reset} className="mt-2 inline-flex h-9 items-center gap-1.5 rounded-full border border-hairline bg-surface px-3.5 text-[13px] font-medium text-ink hover:bg-fill transition-colors focus-visible:outline-none focus-visible:shadow-ring" data-testid="support-start-over">
                        <RotateCcw className="h-3.5 w-3.5" /> {t("sup.new_conversation")}
                      </button>
                    </div>
                  )}
                </>
              }
            >
              {/* Greeting (client-side, not persisted) + quick starts */}
              {loaded && (
                <div className="flex flex-col items-start" data-testid="support-greeting">
                  <p className="flex items-center gap-1.5 text-[11.5px] text-quiet mb-1"><span className="h-1.5 w-1.5 rounded-full bg-ink/30" />{t("sup.assistant")}</p>
                  <div className="max-w-[86%] rounded-[18px] rounded-bl-[6px] bg-fill px-3.5 py-2.5 text-[14px] leading-[1.5] text-ink">{greeting}</div>
                  {messages.length === 0 && !typing && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="support-quick-starts">
                      {QUICK.map((k, i) => (
                        <button key={k} type="button" onClick={() => send(t(k))} disabled={busy} className="h-8 rounded-full border border-hairline bg-surface px-3 text-[12.5px] font-medium text-ink hover:border-ink/30 hover:bg-fill transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:shadow-ring" data-testid={`support-quick-${i + 1}`}>
                          {t(k)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Thread>

            {/* Composer */}
            {!closed && <Composer onSend={send} busy={busy} autoFocus={!mobile} placeholder={humanOwned(kase) ? t("sup.placeholder_person") : t("sup.placeholder")} hint={t("sup.privacy")} testId="support-composer" className={mobile ? "safe-bottom" : ""} />}
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
}
